
import React from "react";

interface UpgradeSelectorProps {
  solarOnly: boolean;
  setSolarOnly: (val: boolean) => void;
  solarPlusAC: boolean;
  setSolarPlusAC: (val: boolean) => void;
  solarACInsulation: boolean;
  setSolarACInsulation: (val: boolean) => void;
  solarKw: number;
  setSolarKw: (val: number) => void;
}

export default function UpgradeSelector({
  solarOnly,
  setSolarOnly,
  solarPlusAC,
  setSolarPlusAC,
  solarACInsulation,
  setSolarACInsulation,
  solarKw,
  setSolarKw,
}: UpgradeSelectorProps) {
  const handleToggle = (option: "solar" | "solarAC" | "solarACIns") => {
    try {
      switch (option) {
        case "solar":
          setSolarOnly?.(!solarOnly);
          break;
        case "solarAC":
          setSolarPlusAC?.(!solarPlusAC);
          break;
        case "solarACIns":
          setSolarACInsulation?.(!solarACInsulation);
          break;
        default:
          console.warn(`UpgradeSelector: unknown toggle option "${option}"`);
      }
    } catch (err) {
      console.error("UpgradeSelector: error toggling option", err);
    }
  };

  const handleKwChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (Number.isNaN(value)) {
      console.warn("UpgradeSelector: received NaN for solarKw");
      return;
    }
    const clamped = Math.min(Math.max(value, 3), 15);
    setSolarKw?.(clamped);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-red-light">
          <i className="ri-tools-line text-brand-red text-xl"></i>
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900">
            Select Upgrade Packages
          </h3>
          <p className="text-xs text-gray-500">
            Compare savings across different combinations
          </p>
        </div>
      </div>

      {/* Solar Only */}
      <button
        onClick={() => handleToggle("solar")}
        className={`w-full text-left rounded-2xl p-5 border-2 transition-all duration-300 cursor-pointer ${
          solarOnly
            ? "border-brand-red bg-brand-red-light shadow-lg shadow-brand-red/10"
            : "border-gray-200 bg-white hover:border-gray-300"
        }`}
      >
        <div className="flex items-start gap-4">
          <div
            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
              solarOnly ? "border-brand-red bg-brand-red" : "border-gray-300"
            }`}
          >
            {solarOnly && (
              <i className="ri-check-line text-white text-sm"></i>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-gold-light">
                <i className="ri-sun-line text-brand-gold text-lg"></i>
              </div>
              <h4 className="font-bold text-gray-900">Solar Panels Only</h4>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Offset your electricity with clean solar energy. Eliminate or
              drastically reduce your electric bill with Florida&apos;s 230+
              sunny days per year.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                30% Federal Tax Credit
              </span>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                No Sales Tax
              </span>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                Net Metering
              </span>
            </div>
          </div>
        </div>
      </button>

      {/* Solar + New AC */}
      <button
        onClick={() => handleToggle("solarAC")}
        className={`w-full text-left rounded-2xl p-5 border-2 transition-all duration-300 cursor-pointer ${
          solarPlusAC
            ? "border-brand-red bg-brand-red-light shadow-lg shadow-brand-red/10"
            : "border-gray-200 bg-white hover:border-gray-300"
        }`}
      >
        <div className="flex items-start gap-4">
          <div
            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
              solarPlusAC ? "border-brand-red bg-brand-red" : "border-gray-300"
            }`}
          >
            {solarPlusAC && (
              <i className="ri-check-line text-white text-sm"></i>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-gold-light">
                <i className="ri-sun-line text-brand-gold text-lg"></i>
              </div>
              <span className="text-gray-400 font-medium">+</span>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-navy-light">
                <i className="ri-temp-cold-line text-brand-navy text-lg"></i>
              </div>
              <h4 className="font-bold text-gray-900">
                Solar + High-Efficiency AC
              </h4>
              <span className="text-xs bg-brand-red text-white px-2 py-1 rounded-full font-bold ml-1 whitespace-nowrap">
                POPULAR
              </span>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Pair solar with a new 20+ SEER2 air conditioner. Your new AC uses
              up to 50% less energy, and solar covers the rest. Maximum bill
              reduction.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                Up to $2,600 in Tax Credits
              </span>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                Utility Rebates
              </span>
            </div>
          </div>
        </div>
      </button>

      {/* Solar + AC + Insulation */}
      <button
        onClick={() => handleToggle("solarACIns")}
        className={`w-full text-left rounded-2xl p-5 border-2 transition-all duration-300 cursor-pointer ${
          solarACInsulation
            ? "border-brand-red bg-brand-red-light shadow-lg shadow-brand-red/10"
            : "border-gray-200 bg-white hover:border-gray-300"
        }`}
      >
        <div className="flex items-start gap-4">
          <div
            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
              solarACInsulation
                ? "border-brand-red bg-brand-red"
                : "border-gray-300"
            }`}
          >
            {solarACInsulation && (
              <i className="ri-check-line text-white text-sm"></i>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-gold-light">
                <i className="ri-sun-line text-brand-gold text-lg"></i>
              </div>
              <span className="text-gray-400 font-medium">+</span>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-navy-light">
                <i className="ri-temp-cold-line text-brand-navy text-lg"></i>
              </div>
              <span className="text-gray-400 font-medium">+</span>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-red-light">
                <i className="ri-home-4-line text-brand-red text-lg"></i>
              </div>
              <h4 className="font-bold text-gray-900">Complete Package</h4>
              <span className="text-xs bg-brand-navy text-white px-2 py-1 rounded-full font-bold ml-1 whitespace-nowrap">
                BEST VALUE
              </span>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              The ultimate energy savings combo. Solar + new AC + R-38 attic
              insulation. Insulation reduces heat transfer by up to 40%, meaning
              your new AC works even less.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                Up to $3,800 in Tax Credits
              </span>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                Up to $800 Utility Rebate
              </span>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                Max Savings
              </span>
            </div>
          </div>
        </div>
      </button>

      {/* Solar System Size */}
      <div className="bg-brand-cream rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-navy-light">
            <i className="ri-sun-line text-brand-navy text-xl"></i>
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">
              Solar System Size
            </h3>
            <p className="text-xs text-gray-500">
              Average Florida home: 8-12 kW
            </p>
          </div>
        </div>

        <input
          type="range"
          min={3}
          max={15}
          step={0.5}
          value={solarKw}
          onChange={handleKwChange}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-red"
        />

        <div className="flex justify-between items-center mt-3">
          <span className="text-xs text-gray-500">3 kW</span>
          <div className="text-center">
            <span className="text-3xl font-bold text-brand-red">{solarKw}</span>
            <span className="text-sm text-gray-600 ml-1">kW</span>
          </div>
          <span className="text-xs text-gray-500">15 kW</span>
        </div>

        <p className="text-xs text-gray-500 mt-2 text-center">
          ~{Math.round(solarKw * 4.5)} panels &bull; ~
          {Math.round(solarKw * 4.5 * 18)} sq ft of roof space
        </p>
      </div>
    </div>
  );
}
